import React, { useEffect, useRef, useState } from "react";

interface SubMenuProps {
  icon: React.ReactNode;
  label?: string;
  children: React.ReactNode;
}

export const SubMenu = ({ icon, label, children }: SubMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<number>();
  const submenuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }
    setIsOpen(true);
  };

  const scheduleClose = () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };

  const handleMouseLeave = () => {
    scheduleClose();
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className="p-2 mx-1 rounded transition-colors flex items-center text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
        title={label}
      >
        {icon}
        {label && <span className="ml-1 text-xs">{label}</span>}
      </button>
      {isOpen && (
        <div
          ref={submenuRef}
          className="absolute z-50 bottom-full left-0 mb-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-1 flex flex-row gap-1 whitespace-nowrap"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && typeof child.type !== "string") {
              return React.cloneElement(child as React.ReactElement<any>, {
                style: { display: "inline-flex" },
              });
            }
            return child;
          })}
        </div>
      )}
    </div>
  );
};
